import { getInputValue } from "@datepicker-react/hooks"
import {
  differenceInDays,
  format,
  isSameMonth,
  isToday,
  isYesterday,
  parseISO,
  toDate,
} from "date-fns"

export const getDateValue = (date: Date | null) => {
  try {
    return getInputValue(date, "MMM dd", "")
  } catch (e) {
    return ""
  }
}
export const formatDate = (date: string) => {
  try {
    return getDateValue(toDate(parseISO(date)))
  } catch (e) {
    return ""
  }
}

export function parseISODate(date: string | null) {
  if (date) {
    return parseISO(date)
  }
  return date
}

export function diffDays(startDate: Date, endDate: Date) {
  return differenceInDays(endDate, startDate)
}

export const getAge = (dateString: Date | string) => {
  const today = new Date()
  const birthDate = new Date(dateString)
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

// Convert the date PST time zone
export const convertToPTFormat = (dateString: number | string) => {
  return new Date(dateString)?.toLocaleDateString("en", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC",
    hour12: false,
  })
}

// Subtract 36 days from the check-in date to get the future date
export const subtractDays = (date: string, days: number) => {
  const convertedDate = convertToPTFormat(date) // Convert to PST format
  const result = new Date(convertedDate) // Convert back to Date object
  result.setDate(result.getDate() - days) // Subtract days
  return result // Return the new date
}

// Get the formatted date for search bar
export const getFormattedDate = (
  startDate: string | null,
  endDate: string | null,
): {
  searchStartDate: string
  searchEndDate: string
} => {
  if (
    startDate &&
    endDate &&
    startDate !== "null" &&
    startDate !== null &&
    endDate !== "null" &&
    endDate !== null
  ) {
    return {
      searchStartDate: formatDate(startDate),
      searchEndDate: formatDate(endDate),
    }
  }
  return { searchStartDate: "", searchEndDate: "" }
}

export const formatDateRange = (startDate: string, endDate: string) => {
  const parsedStartDate = parseISO(startDate || "")
  const parsedEndDate = parseISO(endDate || "")

  const startDateFormatted = format(parsedStartDate, "MMM dd")
  const endDateFormatted = format(parsedEndDate, "dd")

  return isSameMonth(parsedStartDate, parsedEndDate)
    ? `${startDateFormatted}-${endDateFormatted}`
    : `${startDateFormatted} - ${format(parsedEndDate, "MMM dd")}`
}

export function formatDateRangeWithoutLeadingZero(
  start: string | null,
  end: string | null,
) {
  if (!start || start === "null") return ""

  const startDate = parseISO(start)
  const startFormatted = format(startDate, "MMM d") // removes leading zero

  // No end date → "Nov 3 -"
  if (!end || end === "null") {
    return `${startFormatted} -`
  }

  const endDate = parseISO(end)

  // If same month → show only day for second date
  const endFormatted = isSameMonth(startDate, endDate)
    ? format(endDate, "d")
    : format(endDate, "MMM d")

  return `${startFormatted} - ${endFormatted}` // Nov 3 - 23, Nov 23 - Dec 9
}

export const getDateLabel = (timestamp: number) => {
  const date = new Date(timestamp)

  if (isToday(date)) {
    return format(date, "hh:mm a") // Show time if it's today
  }

  if (isYesterday(date)) {
    return "Yesterday" // Show "Yesterday" if it's from yesterday
  }

  return format(date, "MMM dd, yyyy") // Show full date if it's older than yesterday
}

export function formatCancellationTimestamps(timestamps: number[]) {
  if (!Array.isArray(timestamps)) return []

  return timestamps.map((timestamp) => {
    // Convert seconds to milliseconds if needed
    const isSeconds = timestamp < 1e12
    const adjustedTimestamp = isSeconds ? timestamp * 1000 : timestamp

    const parsedDate = new Date(adjustedTimestamp)

    return {
      date: format(parsedDate, "MMM dd, yyyy"),
      time: format(parsedDate, "hh:mm a"),
    }
  })
}
